import { useMemo } from 'react';
import { formatRelativeAge } from '@/lib/format';
import { resolveCheckLabel } from '@/lib/status';
import type { NavBadge } from '@/modules';
import { useNow } from './use-now';
import { useOverview } from './use-overview';

/**
 * How recently an event must have arrived for the sidebar to claim every system is
 * receiving.
 *
 * @since 1.0.0
 */
export const RECEIVING_WINDOW_SECONDS = 900;

/**
 * Everything the app shell shows outside the routed page.
 *
 * @since 1.0.0
 */
export interface ShellStatus {
    badgeCounts: Partial<Record<NavBadge, number>>;
    statusLine: string;
    lastEventLabel: string;
    resolveClientName: (slug: string) => string;
    resolveCheckName: (slug: string) => string;
}

/**
 * Derives the sidebar badges, the receiving footer and the slug-to-name lookups the drawer
 * needs. A failing overview request degrades the chrome instead of unmounting it.
 *
 * @since 1.0.0
 * @returns the shell status
 */
export function useShellStatus(): ShellStatus {
    const { data } = useOverview({ throwOnError: false });
    const now = useNow();

    return useMemo<ShellStatus>(() => {
        const clientNames = new Map<string, string>(
            data?.clients.map((client): [string, string] => [client.slug, client.name]) ?? [],
        );
        const checkNames = new Map<string, string>(
            data?.checkColumns.map((column): [string, string] => [column.slug, column.name]) ?? [],
        );

        const badgeCounts: Partial<Record<NavBadge, number>> = {};

        if (data) {
            badgeCounts['open-alerts'] = data.kpis.openCritical + data.kpis.openWarning;
            badgeCounts['live-checks'] = data.kpis.checksRunning;
            badgeCounts['client-count'] = data.kpis.clientCount;
        }

        const lastEventAt = data?.lastEventAt ?? null;
        const isReceiving = lastEventAt !== null && now - lastEventAt <= RECEIVING_WINDOW_SECONDS;

        return {
            badgeCounts,
            statusLine: isReceiving ? 'All systems receiving' : 'No recent events',
            lastEventLabel:
                lastEventAt === null ? 'No events yet' : `Last event ${formatRelativeAge(lastEventAt, now)} ago`,
            resolveClientName: (slug: string) => clientNames.get(slug) ?? slug,
            resolveCheckName: (slug: string) => resolveCheckLabel(slug, checkNames),
        };
    }, [data, now]);
}
